import { useMemo } from "react";
import trpc, { TrpcOutput } from "@/utils/trpc";
import useCompletedByItemId from "./use-completed-by-item-id";
import useCurrentModuleItemId from "./use-current-module-item-id";

type ModuleItem = NonNullable<TrpcOutput["course"]["getModules"]>[number]["subModules"][number]["items"][number];

/** Pure gating rule, extracted so it can be unit tested without mounting the hook. */
export function shouldShowMarkAsDone(params: {
  currentModuleItemId: string | null;
  currentItem: ModuleItem | null;
  isProgressionSuccess: boolean;
}): boolean {
  const { currentModuleItemId, currentItem, isProgressionSuccess } = params;
  const requiresManualMark =
    !!currentItem?.completion_requirement && currentItem?.completion_requirement?.type === "must_mark_done";

  if (!currentModuleItemId) return false;
  if (!requiresManualMark) return false;
  if (!isProgressionSuccess) return false;

  return true;
}

/**
 * Determines whether the "Mark as done" control should be rendered for the current module item,
 * so callers can decide whether to pass it as footerActions before it mounts.
 */
export default function useShouldShowMarkAsDone(): boolean {
  const currentModuleItemId = useCurrentModuleItemId();

  const { data: modules } = trpc.course.getModules.useQuery(undefined, { staleTime: Infinity });
  const { isProgressionSuccess } = useCompletedByItemId();

  const currentItem = useMemo(() => {
    if (!modules || !currentModuleItemId) return null;
    for (const mod of modules) {
      for (const sub of mod.subModules) {
        const found = sub.items.find(it => String(it.id) === currentModuleItemId);
        if (found) return found;
      }
    }
    return null;
  }, [modules, currentModuleItemId]);

  return shouldShowMarkAsDone({ currentModuleItemId, currentItem, isProgressionSuccess });
}
